import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Code,
  HStack,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { useOrderStateConsistency } from '@/hooks/useOrderConsistencyData'
import { formatDateTime } from '@/utils/formatUtils'

type OrderStateConsistencyTabProps = {
  orderId: string | null
  isActive: boolean
}

export function OrderStateConsistencyTab({
  orderId,
  isActive,
}: OrderStateConsistencyTabProps) {
  const { t } = useTranslation()
  const query = useOrderStateConsistency(orderId, { enabled: isActive })
  const data = query.data

  if (query.isLoading && !data) {
    return (
      <Box py={10} textAlign="center">
        <Spinner />
      </Box>
    )
  }

  if (query.error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {query.error instanceof Error ? query.error.message : t('common.operationFailed')}
      </Alert>
    )
  }

  if (!data) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        {t('orders.lifecycle.stateConsistency.noData')}
      </Alert>
    )
  }

  const statusColor = data.consistencyStatus === 'consistent'
    ? 'green'
    : data.consistencyStatus === 'mismatch'
      ? 'red'
      : 'gray'

  return (
    <VStack align="stretch" spacing={4}>
      <HStack justify="space-between" align="start" flexWrap="wrap">
        <Box>
          <Text fontSize="sm" color="gray.500">
            {t('orders.lifecycle.stateConsistency.status')}
          </Text>
          <Badge mt={1} colorScheme={statusColor}>
            {t(`orders.lifecycle.stateConsistency.statuses.${data.consistencyStatus}`)}
          </Badge>
        </Box>
        <Text fontSize="xs" color="gray.500">
          {t('monitoring.sideEffectJobs.observedAt', {
            time: formatDateTime(data.observedAt),
          })}
        </Text>
      </HStack>

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
        <Stat borderWidth="1px" borderRadius="md" p={3}>
          <StatLabel fontSize="xs">
            {t('orders.lifecycle.stateConsistency.stateVersion')}
          </StatLabel>
          <StatNumber fontSize="xl">{data.stateVersion}</StatNumber>
        </Stat>
        <Stat borderWidth="1px" borderRadius="md" p={3}>
          <StatLabel fontSize="xs">
            {t('orders.lifecycle.stateConsistency.eventCount')}
          </StatLabel>
          <StatNumber fontSize="xl">{data.eventCount}</StatNumber>
        </Stat>
        <Stat borderWidth="1px" borderRadius="md" p={3}>
          <StatLabel fontSize="xs">
            {t('orders.lifecycle.stateConsistency.latestSequence')}
          </StatLabel>
          <StatNumber fontSize="xl">{data.latestEventSequence ?? t('common.na')}</StatNumber>
        </Stat>
        <Stat borderWidth="1px" borderRadius="md" p={3}>
          <StatLabel fontSize="xs">
            {t('orders.lifecycle.stateConsistency.currentStatus')}
          </StatLabel>
          <StatNumber fontSize="md">{data.statusId}</StatNumber>
        </Stat>
      </SimpleGrid>

      <Box borderWidth="1px" borderRadius="md" p={4}>
        <VStack align="stretch" spacing={2}>
          <Text fontSize="sm" fontWeight="semibold">
            {t('orders.lifecycle.stateConsistency.latestEvent')}
          </Text>
          <HStack justify="space-between" align="start" flexWrap="wrap">
            <Text fontSize="sm">
              {t('orders.lifecycle.stateConsistency.eventType')}: {' '}
              {data.latestEventType ?? t('common.na')}
            </Text>
            <Text fontSize="sm">
              {t('orders.lifecycle.stateConsistency.source')}: {' '}
              {data.latestEventSource ?? t('common.na')}
            </Text>
            <Text fontSize="sm">
              {t('orders.lifecycle.stateConsistency.createdAt')}: {' '}
              {data.latestEventAt ? formatDateTime(data.latestEventAt) : t('common.na')}
            </Text>
          </HStack>
          {data.latestEventId && <Code fontSize="xs">{data.latestEventId}</Code>}
        </VStack>
      </Box>

      {data.mismatchReasons.length > 0 && (
        <Alert status="warning" borderRadius="md" alignItems="flex-start">
          <AlertIcon mt={1} />
          <VStack align="stretch" spacing={1}>
            <Text fontWeight="semibold">
              {t('orders.lifecycle.stateConsistency.mismatchReasons')}
            </Text>
            {data.mismatchReasons.map(reason => (
              <Text key={reason} fontSize="sm">
                {t(`orders.lifecycle.stateConsistency.reasons.${reason}`, {
                  defaultValue: reason,
                })}
              </Text>
            ))}
          </VStack>
        </Alert>
      )}
    </VStack>
  )
}
